
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useResellerPlans } from '@/hooks/license/useResellerPlans';
import { v4 as uuidv4 } from 'uuid';
import { useEmailConfigManagement } from '@/hooks/useEmailConfigManagement';
import { useRevenueSplitManagement } from '@/hooks/useRevenueSplitManagement';
import { useBranding } from '@/hooks/useBranding';
import type { 
  LicenseSettings, 
  RevenueSplit,
  MasterLicenseSettingsContextProps 
} from '@/types/masterLicenseSettings';

const MasterLicenseSettingsContext = createContext<MasterLicenseSettingsContextProps | undefined>(undefined);

export const useMasterLicenseSettings = () => {
  const context = useContext(MasterLicenseSettingsContext);
  if (!context) {
    throw new Error('useMasterLicenseSettings must be used within a MasterLicenseSettingsProvider');
  }
  return context;
};

interface MasterLicenseSettingsProviderProps {
  children: React.ReactNode;
  isLoading: boolean;
}

export const MasterLicenseSettingsProvider: React.FC<MasterLicenseSettingsProviderProps> = ({ 
  children, 
  isLoading 
}) => {
  const { resellerPlans } = useResellerPlans(true);
  const emailConfig = useEmailConfigManagement();
  const revenueSplits = useRevenueSplitManagement();
  const { logo } = useBranding();
  
  const [licenseSettings, setLicenseSettings] = useState<LicenseSettings>({
    duration: '365',
    type: 'reseller',
    autoRenewal: false,
    stripeEnabled: false,
    stripeFeePercentage: 2.9,
    revenueSplits: [
      {
        id: uuidv4(),
        volumeThreshold: 0,
        masterLicensePercentage: 80,
        resellerPercentage: 20,
        isActive: true
      },
      {
        id: uuidv4(),
        volumeThreshold: 5000,
        masterLicensePercentage: 75,
        resellerPercentage: 25,
        isActive: true
      },
      {
        id: uuidv4(),
        volumeThreshold: 10000,
        masterLicensePercentage: 70,
        resellerPercentage: 30,
        isActive: true
      },
      {
        id: uuidv4(),
        volumeThreshold: 25000,
        masterLicensePercentage: 65,
        resellerPercentage: 35,
        isActive: true
      },
      {
        id: uuidv4(),
        volumeThreshold: 50000,
        masterLicensePercentage: 60,
        resellerPercentage: 40,
        isActive: true
      },
      {
        id: uuidv4(),
        volumeThreshold: 100000,
        masterLicensePercentage: 55,
        resellerPercentage: 45,
        isActive: true
      }
    ],
    emailConfigs: [
      {
        id: uuidv4(),
        name: 'Default SMTP',
        provider: 'smtp',
        host: 'smtp.example.com',
        port: 587,
        username: 'noreply@example.com',
        password: 'password123',
        encryption: 'tls',
        fromEmail: 'noreply@example.com',
        fromName: 'Master License Portal',
        isDefault: true,
        isActive: true
      }
    ]
  });

  useEffect(() => {
    if (resellerPlans.length > 0) {
      const resellerProPlan = resellerPlans.find(plan => plan.name === 'Reseller Pro');
      if (resellerProPlan) {
        setLicenseSettings(prev => ({
          ...prev,
          planId: resellerProPlan.id,
          type: 'reseller'
        }));
      }
    }
  }, [resellerPlans]);

  useEffect(() => {
    if (logo?.file_url) {
      setLicenseSettings(prev => ({
        ...prev,
        logoUrl: logo.file_url
      }));
    }
  }, [logo?.file_url]);

  const handleSettingChange = (field: string, value: string | boolean | number) => {
    setLicenseSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRevenueSplitUpdate = (id: string, updates: Partial<RevenueSplit>) => {
    setLicenseSettings(prev => ({
      ...prev,
      revenueSplits: (prev.revenueSplits || []).map(split => 
        split.id === id ? { ...split, ...updates } : split
      )
    }));
  };

  const handleRevenueSplitDelete = (id: string) => {
    setLicenseSettings(prev => ({
      ...prev,
      revenueSplits: (prev.revenueSplits || []).filter(split => split.id !== id)
    }));
  };

  return (
    <MasterLicenseSettingsContext.Provider 
      value={{ 
        licenseSettings,
        handleSettingChange,
        handleRevenueSplitAdd: (split) => {
          const newSplit = revenueSplits.handleRevenueSplitAdd(split);
          setLicenseSettings(prev => ({
            ...prev,
            revenueSplits: [...(prev.revenueSplits || []), newSplit]
          }));
        },
        handleRevenueSplitUpdate,
        handleRevenueSplitDelete,
        handleEmailConfigAdd: emailConfig.handleEmailConfigAdd,
        handleEmailConfigUpdate: emailConfig.handleEmailConfigUpdate,
        handleEmailConfigDelete: emailConfig.handleEmailConfigDelete,
        handleTestEmailConnection: emailConfig.handleTestEmailConnection,
        isLoading,
        isSavingRevenueSplits: revenueSplits.isSavingRevenueSplits,
        isSavingEmailConfig: emailConfig.isSavingEmailConfig,
        isTestingEmailConnection: emailConfig.isTestingEmailConnection,
        handleSaveRevenueSplits: revenueSplits.handleSaveRevenueSplits
      }}
    >
      {children}
    </MasterLicenseSettingsContext.Provider>
  );
};
